import type { AccountUser } from "../lib/auth";
import type { PortalSummary } from "../lib/portal";
import type { AccountCredit } from "../lib/credit";
import { useT } from "../i18n/I18nProvider";
import { OverviewSection } from "./sections/OverviewSection";
import { ProfileSection } from "./sections/ProfileSection";
import { SecuritySection } from "./sections/SecuritySection";
import { PreferencesSection } from "./sections/PreferencesSection";
import { NotificationsSection } from "./sections/notifications";
import { PrivacySection } from "./sections/PrivacySection";
import { BillingSection } from "./sections/BillingSection";
import { InvoicesSection } from "./sections/invoices";
import { EstimatesSection } from "./sections/estimates";
import { StatementSection } from "./sections/StatementSection";
import { ProjectsSection } from "./sections/projects";
import { SupportSection } from "./sections/support";
import { ContractsSection } from "./sections/contracts";
import { PortalOverview } from "./sections/overview";
import { UsageSection } from "./sections/UsageSection";
import { ApiKeysSection } from "./sections/ApiKeysSection";
import { ReferralSection } from "./sections/ReferralSection";
import { PreviewAccountNotice } from "./PreviewAccountNotice";
import { PersonalPage } from "./PersonalPage";
import { PageHead } from "./ui";
import type { TabKey } from "./tabs";

/**
 * The person's own screens. In the hub they sit under the greeting and beside a
 * side menu that says which one is open, one screen per tab. The portal draws
 * all of them as one page (`PersonalPage`); a staff preview of the portal gets
 * a title and a notice for the one screen it asked for.
 */
const PERSONAL_TABS: ReadonlySet<TabKey> = new Set<TabKey>([
  "profile", "security", "notifications", "preferences", "privacy",
]);

/**
 * The screen behind the active tab.
 *
 * One dispatch for every shell, so the question "which screen does this tab
 * open" has a single answer whatever chrome is drawn around it. The layout
 * decides WHICH tab is active and whether the visit may open it; this file only
 * turns that key into a component.
 */
export function SectionView({
  active,
  user,
  preview,
  clientFacing,
  previewAccountTab,
  summary,
  credit,
  referralEnabled,
  onInvite,
  onUserChange,
  onLogout,
}: {
  active: TabKey;
  /** Null only in a staff preview - see `AccountLayout`. */
  user: AccountUser | null;
  /** An operator looking at the portal as the customer would see it. */
  preview: boolean;
  /**
   * The operator's client portal, or a preview of it, rather than Husly's own
   * hub. The two draw a different front page: the hub's is about the person's
   * Husly sites and credit, the portal's is about what a client has to do.
   */
  clientFacing: boolean;
  /**
   * An account tab opened inside a staff preview. Those screens read the
   * signed-in OPERATOR's account, so a notice stands in for them - see
   * `PreviewAccountNotice` for the full reason.
   */
  previewAccountTab: boolean;
  summary: PortalSummary | null;
  /** Account wallet; `null` = the module is off or the user has no account. */
  credit: AccountCredit | null;
  referralEnabled: boolean;
  onInvite: () => void;
  onUserChange: (u: AccountUser) => void;
  onLogout: () => void;
}) {
  const t = useT();
  const personalTab = clientFacing && PERSONAL_TABS.has(active);
  const personalHead = personalTab ? <PageHead title={t(`tabs.${active}`)} /> : null;

  if (previewAccountTab) {
    return (
      <>
        {personalHead}
        <PreviewAccountNotice label={t(`tabs.${active}`)} />
      </>
    );
  }

  if (personalTab && user) {
    return <PersonalPage active={active} user={user} onUserChange={onUserChange} onLogout={onLogout} />;
  }

  return (
    <>
      {/* The portal's front page reads only `portal.*` lists, so it is safe in
          a preview and needs no signed-in person; the hub's reads the person's
          own account and is never drawn without one. */}
      {/* Never the signed-in person in a preview: that is the OPERATOR, and the
          page would greet them by name over a client's records. */}
      {active === "overview" && clientFacing && (
        <PortalOverview user={preview ? null : user} summary={summary} preview={preview} />
      )}
      {active === "overview" && !clientFacing && user && (
        <OverviewSection
          user={user}
          summary={summary}
          credit={credit}
          referralEnabled={referralEnabled}
          onInvite={onInvite}
        />
      )}
      {active === "profile" && user && <ProfileSection user={user} onUserChange={onUserChange} />}
      {active === "security" && user && <SecuritySection user={user} />}
      {active === "notifications" && <NotificationsSection />}
      {active === "preferences" && <PreferencesSection />}
      {active === "privacy" && user && <PrivacySection user={user} onLogout={onLogout} />}
      {active === "billing" && <BillingSection />}
      {active === "usage" && <UsageSection />}
      {active === "referral" && <ReferralSection />}
      {active === "apikeys" && <ApiKeysSection />}
      {active === "invoices" && <InvoicesSection />}
      {active === "estimates" && <EstimatesSection />}
      {active === "statement" && <StatementSection />}
      {/* Which project is open is ordinary URL state (`?project=`) that the
          page reads for itself, including the one a preview link was minted
          from. */}
      {active === "projects" && <ProjectsSection />}
      {active === "support" && <SupportSection />}
      {active === "contracts" && <ContractsSection />}
    </>
  );
}
